// What the web has loaded: the workspace (projects, clients, rules, settings) and time ranges (entries, sessions,
// spans, corrections, tokens). The same shape as the Mac's AppData and RangeData.
import {
  applyEdits, DEFAULT_CONFIG, liveSessionState, type AgentPiece, type BillingProject, type Interval, type ReportItem, type SpanEdit as CoreSpanEdit,
  type TokenCostSettings,
} from '../../../packages/core/src/index.ts';
import type {
  ActivitySpan, AgentSession, Base, Client, Project, ProjectRule, RangeResponse, SessionResponse, SpanEdit, SyncedSettingKey, TimeEntry, UsageTotal,
} from '../api/types.ts';

/**
 * Teams: whose device is whose (a session is its recording device's member's time, spec §9b), who you are and your
 * role. Owners see the team's time; everyone's own screens show their own.
 */
export interface Team {
  deviceMembers: Map<string, string>;
  /** In a workspace of one, devices the server has not described belong to that one member. */
  soleMember: string | null;
  you: string | null;
  role: 'owner' | 'member';
  /** Historical members remain separate people when time is billed. */
  size: number;
}

export const soloTeam = (): Team => ({ deviceMembers: new Map(), soleMember: null, you: null, role: 'owner', size: 1 });

export interface Workspace {
  projects: Project[];
  clients: Client[];
  rules: ProjectRule[];
  settings: Map<SyncedSettingKey, string>;
  members: { id: string; name: string; role?: 'owner' | 'member'; removed?: boolean }[];
  team: Team;
}

export function workspaceFrom(base: Base): Workspace {
  return {
    projects: base.projects,
    clients: base.clients,
    rules: base.projectRules.filter((r) => r.deletedAt === null),
    settings: new Map(base.settings.map((s) => [s.key, s.value])),
    members: base.members,
    team: {
      deviceMembers: new Map((base.devices ?? []).map((d) => [d.id, d.memberId])),
      soleMember: base.members.length === 1 ? base.members[0]!.id : null,
      you: base.you?.memberId ?? null,
      role: base.you?.role ?? 'member',
      size: Math.max(1, base.members.length),
    },
  };
}

export const emptyWorkspace = (): Workspace => ({ projects: [], clients: [], rules: [], settings: new Map(), members: [], team: soloTeam() });

/** Whether the signed-in person may change projects, clients, rules and settings (members track their own time only). */
export const isOwner = (ws: Workspace): boolean => ws.team.role === 'owner';

export function project(ws: Workspace, id: string | null | undefined): Project | undefined {
  return id ? ws.projects.find((p) => p.id === id) : undefined;
}

export function client(ws: Workspace, id: string | null | undefined): Client | undefined {
  return id ? ws.clients.find((c) => c.id === id) : undefined;
}

export function activeProjects(ws: Workspace): Project[] {
  return ws.projects.filter((p) => p.deletedAt === null && p.archivedAt === null);
}

/** The project the next timer starts with: the chosen one while it is active, else the first active project (as in the apps). */
export function timerDraftProject(ws: Workspace, draftProjectId: string | null): Project | undefined {
  const active = activeProjects(ws);
  return active.find((p) => p.id === draftProjectId) ?? active[0];
}

export function billingProjects(ws: Workspace): BillingProject[] {
  return ws.projects.map((p) => ({ id: p.id, rateCents: p.rateCents, billable: p.billable, roundingMinutes: p.roundingMinutes }));
}

/** Token costs as their own invoice line (spec §9c): `{"markup":1500,"usdRate":9200}`; null while off. */
export function tokenSettings(ws: Workspace): TokenCostSettings | null {
  const raw = ws.settings.get('tokenCosts');
  if (!raw) return null;
  try {
    const value = JSON.parse(raw) as { usdRate?: unknown; markup?: unknown };
    const usdRate = Number(value.usdRate);
    const markup = Number(value.markup ?? 0);
    if (!Number.isInteger(usdRate) || usdRate < 1 || usdRate > 1_000_000 || !Number.isInteger(markup) || markup < 0 || markup > 100_000) return null;
    return { usdRate, markup };
  } catch {
    return null;
  }
}

/** The stored form, byte for byte as the Mac writes it. */
export const tokenSettingValue = (s: TokenCostSettings): string => JSON.stringify({ markup: s.markup, usdRate: s.usdRate });

/** VAT in basis points (19 % = 1900); 0 for the small-business exemption. */
export function vatBasisPoints(ws: Workspace): number {
  const raw = ws.settings.get('vatPercent');
  if (raw === 'exempt') return 0;
  const value = raw === undefined ? NaN : Number(raw.replace(',', '.'));
  return Number.isFinite(value) ? Math.round(value * 100) : 1900;
}

export interface RangeData {
  range: Interval;
  entries: TimeEntry[];
  sessions: AgentSession[];
  /** Spans overlapping the range, per session. */
  spans: Map<string, ActivitySpan[]>;
  /** Corrections per session, reverted ones included (history). */
  edits: Map<string, SpanEdit[]>;
  usage: Map<string, UsageTotal[]>;
  /** Whose device is whose, for billing per member. */
  team?: Team;
}

/** Whose time a session recorded on `deviceId` is. */
export function memberOfDevice(data: RangeData, deviceId: string): string | null {
  return data.team?.deviceMembers.get(deviceId) ?? data.team?.soleMember ?? null;
}

/** Only a workspace with several members bills per member (spec §9b); alone, all time is one person's. */
export const isTeamData = (data: RangeData): boolean => (data.team?.size ?? 1) > 1;

/** The member an entry's or a piece's time counts for in billing: nobody in particular when alone. */
export function billingMember(data: RangeData, memberId: string | null | undefined): string | null {
  return isTeamData(data) ? memberId ?? null : null;
}

/** Only one member's time: everyone's own screens in a team (reports can show the team's). */
export function onlyMember(data: RangeData, memberId: string): RangeData {
  const sessions = data.sessions.filter((s) => memberOfDevice(data, s.deviceId) === memberId);
  const ids = new Set(sessions.map((s) => s.id));
  const keep = <T>(map: Map<string, T[]>) => new Map([...map].filter(([id]) => ids.has(id)));
  return { ...data, entries: data.entries.filter((e) => e.memberId === memberId), sessions, spans: keep(data.spans), edits: keep(data.edits), usage: keep(data.usage) };
}

export function group<T>(list: readonly T[], key: (item: T) => string): Map<string, T[]> {
  const out = new Map<string, T[]>();
  for (const item of list) {
    const k = key(item);
    const bucket = out.get(k);
    if (bucket) bucket.push(item);
    else out.set(k, [item]);
  }
  return out;
}

export function rangeFrom(response: Pick<RangeResponse, 'entries' | 'sessions' | 'spans' | 'edits' | 'usage'>, range: Interval, team?: Team): RangeData {
  return {
    range,
    ...(team ? { team } : {}),
    entries: response.entries.filter((e) => e.deletedAt === null).sort((a, b) => a.startAt - b.startAt),
    sessions: [...response.sessions].sort((a, b) => a.startedAt - b.startedAt),
    spans: group([...response.spans].sort((a, b) => a.startAt - b.startAt), (s) => s.sessionId),
    edits: group([...response.edits].sort((a, b) => a.createdAt - b.createdAt), (e) => e.sessionId),
    usage: group(response.usage, (u) => u.sessionId),
  };
}

export function sessionRangeFrom(response: SessionResponse, team?: Team): RangeData {
  return rangeFrom({ entries: [], sessions: [response.session, ...response.children], spans: response.spans, edits: response.edits, usage: response.usage },
    { start: 0, end: Number.MAX_SAFE_INTEGER }, team);
}

export const emptyRange = (range: Interval): RangeData => ({ range, entries: [], sessions: [], spans: new Map(), edits: new Map(), usage: new Map() });

export function runningEntry(data: RangeData, memberId?: string): TimeEntry | undefined {
  return data.entries.find((e) => e.endAt === null && e.deletedAt === null && (memberId === undefined || e.memberId === memberId));
}

// MARK: Live

/**
 * After this long without news from a working session, its open span stops counting here. Every session reaches
 * the web through its device's sync, so a Mac that went to sleep mid-session looks like this: its stale guard will
 * end the span at the last activity plus the grace period when it wakes. Idle timeout (2 min) plus a minute of sync.
 */
export const QUIET_AFTER_MS = DEFAULT_CONFIG.idleTimeoutMs + 60_000;

export function isQuiet(session: AgentSession, now: number): boolean {
  if (session.pauseAt != null) return session.state === 'working' && now >= session.pauseAt;
  return session.state === 'working' && session.lastActivityAt !== null && now - session.lastActivityAt > QUIET_AFTER_MS;
}

/** Where a span ends for now: its end, or for an open span now (a quiet session's at its last activity plus grace). */
export function spanEnd(session: AgentSession | undefined, span: ActivitySpan, now: number): number {
  if (span.endAt !== null) return span.endAt;
  if (session?.pauseAt != null && span.state === 'working') return Math.max(span.startAt, Math.min(now, session.pauseAt));
  if (session && isQuiet(session, now) && session.lastActivityAt !== null) {
    return Math.max(span.startAt, Math.min(now, session.lastActivityAt + DEFAULT_CONFIG.staleGraceMs));
  }
  return now;
}

function coreEdit(e: SpanEdit): CoreSpanEdit {
  return { id: e.id, session: e.sessionId, kind: e.kind, start: e.startAt, end: e.endAt, project: e.projectId, createdAt: e.createdAt, revertedAt: e.revertedAt };
}

/** Agent working time as pieces with their project, corrections applied (spec §9a), clipped to the range. */
export function agentPieces(data: RangeData, now: number): AgentPiece[] {
  const pieces: AgentPiece[] = [];
  for (const session of data.sessions) {
    for (const span of data.spans.get(session.id) ?? []) {
      if (span.state !== 'working') continue;
      const end = spanEnd(session, span, now);
      if (end > span.startAt) pieces.push({ session: session.id, project: session.projectId, start: span.startAt, end, member: billingMember(data, memberOfDevice(data, session.deviceId)) });
    }
  }
  const edits = [...data.edits.values()].flat().map(coreEdit);
  const out: AgentPiece[] = [];
  for (const piece of applyEdits(pieces, edits)) {
    const start = Math.max(piece.start, data.range.start);
    const end = Math.min(piece.end, data.range.end);
    if (end > start) out.push({ ...piece, start, end });
  }
  return out;
}

/** Report items: your entries and the agents' working time (corrections applied). */
export function reportItems(data: RangeData, now: number): ReportItem[] {
  const providers = new Map(data.sessions.map((s) => [s.id, s.provider]));
  return [
    ...data.entries.map((e): ReportItem => ({ project: e.projectId, source: 'human', start: e.startAt, end: e.endAt, billable: e.billable, member: billingMember(data, e.memberId) })),
    ...agentPieces(data, now).map((p): ReportItem => ({
      project: p.project, source: 'agent', provider: providers.get(p.session) ?? null, start: p.start, end: p.end, billable: true, member: p.member ?? null,
    })),
  ];
}

// MARK: Providers and sessions

export interface ProviderInfo {
  id: string;
  name: string;
  mono: string;
}

/** Monogram tiles stand in for logos until brand guidelines allow official marks. */
export function providerInfo(id: string): ProviderInfo {
  switch (id) {
    case 'claude-code': return { id, name: 'Claude Code', mono: 'CC' };
    case 'codex': return { id, name: 'Codex', mono: 'CX' };
    case 'cursor': return { id, name: 'Cursor', mono: 'CU' };
    case 'gemini-cli': return { id, name: 'Gemini CLI', mono: 'GE' };
    case 'github-copilot': return { id, name: 'GitHub Copilot', mono: 'GH' };
    default: return { id, name: id, mono: id.slice(0, 2).toUpperCase() };
  }
}

/** "repo · branch" (never derived from prompts on the web), else the provider. */
export function displayTitle(session: AgentSession): string {
  if (session.title) return session.title;
  if (session.repoName) return session.gitBranch ? `${session.repoName} · ${session.gitBranch}` : session.repoName;
  return providerInfo(session.provider).name;
}

export type PillState = 'working' | 'waiting' | 'permission' | 'paused' | 'idle' | 'done' | 'error';

export function pillState(session: AgentSession, now: number): PillState {
  if (session.pauseAt != null && now >= session.pauseAt && (session.state === 'working' || session.state === 'idle')) return 'paused';
  switch (liveSessionState(session.state, session.stateSince, session.lastEventAt, now, session.pauseAt)) {
    case 'working': return 'working';
    case 'waiting_input': return 'waiting';
    case 'waiting_permission': return 'permission';
    case 'idle': return 'idle';
    default: return 'done';
  }
}

/** Tags used before, most used first. */
export function knownTags(entries: readonly TimeEntry[]): string[] {
  const counts = new Map<string, number>();
  for (const e of entries) for (const tag of e.tags) counts.set(tag, (counts.get(tag) ?? 0) + 1);
  return [...counts].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0])).map(([tag]) => tag);
}
